import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  Loader2,
  RotateCw,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { Document, Page } from "react-pdf";
import { ActionButtonGroup, IconButton } from "@doculegis/shared";
import { Button } from "@/components/ui/button";
import { fetchDocumentFile, fetchDocumentVersionFile, fetchThreadAttachmentFile, getDocumentDownloadUrl, getDocumentVersionDownloadUrl, getThreadAttachmentDownloadUrl } from "../api/rag-api.js";
import { buildDocumentOpenUrl } from "../lib/document-open.js";
import { highlightExcerptInPage } from "../lib/highlight-pdf-excerpt.js";
import "../lib/pdf-worker.js";
import "../lib/highlight-pdf-excerpt.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

const ZOOM_STEPS = [0.75, 1, 1.25, 1.5, 1.75, 2] as const;
const MAX_SCROLL_ATTEMPTS = 120;
const ANCHOR_TOLERANCE_PX = 80;

interface PdfDocumentViewerProps {
  documentId?: string;
  documentVersionId?: string;
  attachmentId?: string;
  threadId?: string;
  title: string;
  initialPage?: number;
  highlightText?: string;
  onClose: () => void;
}

function formatDisplayTitle(title: string): string {
  const trimmed = title.trim();
  if (!trimmed) return "Document.pdf";
  return /\.[a-z0-9]{2,5}$/i.test(trimmed) ? trimmed : `${trimmed}.pdf`;
}

interface PdfSource {
  documentId?: string;
  documentVersionId?: string;
  attachmentId?: string;
  threadId?: string;
}

async function loadPdfBlob(source: PdfSource): Promise<Blob> {
  if (source.attachmentId && source.threadId) {
    try {
      return await fetchThreadAttachmentFile(source.threadId, source.attachmentId);
    } catch {
      const { downloadUrl, mimeType } = await getThreadAttachmentDownloadUrl(
        source.threadId,
        source.attachmentId,
      );
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to load attachment (${response.status})`);
      }
      const blob = await response.blob();
      if (!blob.size) {
        throw new Error("Attachment file is empty");
      }
      if (mimeType && !blob.type) {
        return new Blob([await blob.arrayBuffer()], { type: mimeType });
      }
      return blob;
    }
  }

  if (!source.documentId) {
    throw new Error("Document reference is unavailable.");
  }

  if (source.documentVersionId) {
    try {
      return await fetchDocumentVersionFile(source.documentId, source.documentVersionId);
    } catch {
      const { downloadUrl, mimeType } = await getDocumentVersionDownloadUrl(
        source.documentId,
        source.documentVersionId,
      );
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to load document version (${response.status})`);
      }
      const blob = await response.blob();
      if (!blob.size) {
        throw new Error("Document version file is empty");
      }
      if (mimeType && !blob.type) {
        return new Blob([await blob.arrayBuffer()], { type: mimeType });
      }
      return blob;
    }
  }

  try {
    return await fetchDocumentFile(source.documentId);
  } catch {
    const { downloadUrl, mimeType } = await getDocumentDownloadUrl(source.documentId);
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error(`Failed to load document (${response.status})`);
    }
    const blob = await response.blob();
    if (!blob.size) {
      throw new Error("Document file is empty");
    }
    if (mimeType && !blob.type) {
      return new Blob([await blob.arrayBuffer()], { type: mimeType });
    }
    return blob;
  }
}

export function PdfDocumentViewer({
  documentId,
  documentVersionId,
  attachmentId,
  threadId,
  title,
  initialPage = 1,
  highlightText,
  onClose,
}: PdfDocumentViewerProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const pagesColumnRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pendingScrollPage = useRef<number | null>(null);
  const targetInitialPageRef = useRef(initialPage);
  const initialScrollDoneRef = useRef(false);
  const highlightScrolledRef = useRef(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [baseWidth, setBaseWidth] = useState(640);
  const [zoomIndex, setZoomIndex] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(initialPage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const displayTitle = formatDisplayTitle(title);
  const pdfSource = useMemo(
    () => ({ documentId, documentVersionId, attachmentId, threadId }),
    [documentId, documentVersionId, attachmentId, threadId],
  );
  const zoom = ZOOM_STEPS[zoomIndex];
  const pageWidth = Math.floor(baseWidth * zoom);
  const pageNumbers = useMemo(
    () => (numPages > 0 ? Array.from({ length: numPages }, (_, index) => index + 1) : []),
    [numPages],
  );

  const file = useMemo(() => (pdfBlob ? URL.createObjectURL(pdfBlob) : null), [pdfBlob]);

  useEffect(() => {
    targetInitialPageRef.current = initialPage;
    setPageNumber(initialPage);
    pendingScrollPage.current = initialPage;
    initialScrollDoneRef.current = false;
    highlightScrolledRef.current = false;
  }, [initialPage, pdfSource]);

  const highlightTextRef = useRef(highlightText);
  highlightTextRef.current = highlightText;

  const scrollHighlightIntoView = useCallback((span: HTMLElement) => {
    const container = canvasRef.current;
    if (!container) {
      return;
    }

    const top =
      span.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      container.clientHeight / 3;
    container.scrollTop = Math.max(Math.round(top), 0);
  }, []);

  const applyExcerptHighlight = useCallback(() => {
    const excerpt = highlightTextRef.current?.trim();
    if (!excerpt) {
      return;
    }

    const page = targetInitialPageRef.current || initialPage;
    const pageElement = pageRefs.current[page - 1];
    if (!pageElement) {
      return;
    }

    const firstMatch = highlightExcerptInPage(pageElement, excerpt);
    if (firstMatch && initialScrollDoneRef.current && !highlightScrolledRef.current) {
      scrollHighlightIntoView(firstMatch);
      highlightScrolledRef.current = true;
    }
  }, [initialPage, scrollHighlightIntoView]);

  const finishInitialScroll = useCallback((targetPage: number) => {
    setPageNumber(targetPage);
    pendingScrollPage.current = null;
    initialScrollDoneRef.current = true;
    applyExcerptHighlight();
  }, [applyExcerptHighlight]);

  const arePagesReadyUpTo = useCallback((targetPage: number): boolean => {
    for (let index = 0; index < targetPage; index += 1) {
      const element = pageRefs.current[index];
      if (!element || element.offsetHeight <= 0) {
        return false;
      }
    }
    return true;
  }, []);

  const isPageAnchoredAtTop = useCallback(
    (page: number, tolerancePx = ANCHOR_TOLERANCE_PX): boolean => {
      const root = canvasRef.current;
      const element = pageRefs.current[page - 1];
      if (!root || !element) {
        return false;
      }

      return (
        Math.abs(
          element.getBoundingClientRect().top - root.getBoundingClientRect().top,
        ) <= tolerancePx
      );
    },
    [],
  );

  const scrollPageIntoContainer = useCallback((page: number) => {
    const container = canvasRef.current;
    const element = pageRefs.current[page - 1];
    if (!container || !element) {
      return false;
    }

    const top =
      element.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;
    container.scrollTop = Math.max(Math.round(top), 0);
    return true;
  }, []);

  const scrollToPageWhenReady = useCallback(
    (targetPage: number, attempt = 0) => {
      if (numPages <= 0 || pendingScrollPage.current == null) {
        return;
      }

      const clamped = Math.min(Math.max(targetPage, 1), numPages);
      setPageNumber(clamped);
      pendingScrollPage.current = clamped;

      const element = pageRefs.current[clamped - 1];
      const scheduleRetry = () => {
        // Keep pending until anchored; layout/render observers will retry later.
        if (attempt < MAX_SCROLL_ATTEMPTS) {
          requestAnimationFrame(() => scrollToPageWhenReady(clamped, attempt + 1));
        } else {
          scrollPageIntoContainer(clamped);
        }
      };

      if (!arePagesReadyUpTo(clamped) || !element) {
        scheduleRetry();
        return;
      }

      scrollPageIntoContainer(clamped);

      if (isPageAnchoredAtTop(clamped)) {
        finishInitialScroll(clamped);
        return;
      }

      scheduleRetry();
    },
    [
      arePagesReadyUpTo,
      finishInitialScroll,
      isPageAnchoredAtTop,
      numPages,
      scrollPageIntoContainer,
    ],
  );

  useEffect(() => {
    if (!canvasRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width > 0) {
        setBaseWidth(Math.max(Math.floor(width - 40), 360));
      }
    });

    observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      setLoading(true);
      setError(null);
      setPdfBlob(null);
      setIframeUrl(null);
      setNumPages(0);
      pageRefs.current = [];
      targetInitialPageRef.current = initialPage;
      pendingScrollPage.current = initialPage;
      initialScrollDoneRef.current = false;
      highlightScrolledRef.current = false;

      try {
        const blob = await loadPdfBlob(pdfSource);
        if (!cancelled) {
          setPdfBlob(blob);
        }
      } catch (loadError) {
        if (cancelled) return;

        try {
          const { downloadUrl, mimeType } =
            pdfSource.attachmentId && pdfSource.threadId
              ? await getThreadAttachmentDownloadUrl(
                  pdfSource.threadId,
                  pdfSource.attachmentId,
                )
              : pdfSource.documentVersionId && pdfSource.documentId
                ? await getDocumentVersionDownloadUrl(
                    pdfSource.documentId,
                    pdfSource.documentVersionId,
                  )
                : await getDocumentDownloadUrl(pdfSource.documentId!);
          const openUrl = buildDocumentOpenUrl(downloadUrl, {
            mimeType,
            page: String(initialPage),
          });
          setIframeUrl(openUrl);
          initialScrollDoneRef.current = true;
        } catch {
          setError(
            loadError instanceof Error ? loadError.message : "Failed to load PDF",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadPdf();

    return () => {
      cancelled = true;
    };
  }, [pdfSource, initialPage]);

  useEffect(() => {
    return () => {
      if (file) {
        URL.revokeObjectURL(file);
      }
    };
  }, [file]);

  useEffect(() => {
    if (numPages <= 0 || !canvasRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!initialScrollDoneRef.current || pendingScrollPage.current != null) {
          return;
        }

        const mostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!mostVisible?.target) return;

        const page = Number((mostVisible.target as HTMLElement).dataset.page);
        if (page > 0) {
          setPageNumber(page);
        }
      },
      {
        root: canvasRef.current,
        threshold: [0.35, 0.55, 0.75],
      },
    );

    pageRefs.current.forEach((element) => {
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [numPages, pageWidth, rotation, file]);

  useEffect(() => {
    if (numPages <= 0) return;

    if (pendingScrollPage.current == null || initialScrollDoneRef.current) return;
    scrollToPageWhenReady(pendingScrollPage.current);
  }, [numPages, pageWidth, rotation, scrollToPageWhenReady]);

  useEffect(() => {
    const column = pagesColumnRef.current;
    if (!column || numPages <= 0) return;

    const observer = new ResizeObserver(() => {
      if (pendingScrollPage.current != null && !initialScrollDoneRef.current) {
        scrollToPageWhenReady(pendingScrollPage.current);
      }
    });

    observer.observe(column);
    return () => observer.disconnect();
  }, [numPages, pageWidth, rotation, file, scrollToPageWhenReady]);

  function scrollToPage(nextPage: number) {
    if (numPages <= 0) return;
    const clamped = Math.min(Math.max(nextPage, 1), numPages);
    pendingScrollPage.current = null;
    initialScrollDoneRef.current = true;
    targetInitialPageRef.current = clamped;
    setPageNumber(clamped);
    scrollPageIntoContainer(clamped);
  }

  async function handleOpenExternal() {
    try {
      const { downloadUrl, mimeType } =
        pdfSource.attachmentId && pdfSource.threadId
          ? await getThreadAttachmentDownloadUrl(pdfSource.threadId, pdfSource.attachmentId)
          : pdfSource.documentVersionId && pdfSource.documentId
            ? await getDocumentVersionDownloadUrl(
                pdfSource.documentId,
                pdfSource.documentVersionId,
              )
            : await getDocumentDownloadUrl(pdfSource.documentId!);
      const openUrl = buildDocumentOpenUrl(downloadUrl, {
        mimeType,
        page: String(pageNumber),
      });
      window.open(openUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to open document externally");
    }
  }

  function handleDownload() {
    if (!file) return;
    const link = window.document.createElement("a");
    link.href = file;
    link.download = displayTitle;
    link.click();
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--lm-border-subtle)] bg-[var(--bar-bg)] px-3 py-2 backdrop-blur-sm dark:border-white/10 dark:bg-[#0f172a]/90 sm:px-4 sm:py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-white">
            {displayTitle}
            <span className="ml-2 font-normal text-[var(--lm-muted)] dark:text-slate-400">Page {pageNumber}</span>
          </p>
        </div>
        <IconButton
          type="button"
          size="sm"
          title="Close"
          aria-label="Close"
          onClick={onClose}
          className="shrink-0 text-[var(--lm-muted)] hover:text-[var(--lm-foreground-strong)] dark:text-slate-400 dark:hover:text-white"
        >
          <X className="h-4 w-4" />
        </IconButton>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-[var(--lm-border-subtle)] bg-white px-3 py-2 dark:border-white/10 dark:bg-[#111827]/95">
        <ActionButtonGroup>
          <IconButton
            type="button"
            size="sm"
            title="Previous page"
            aria-label="Previous page"
            disabled={pageNumber <= 1 || loading}
            onClick={() => scrollToPage(pageNumber - 1)}
          >
            <ChevronUp className="h-4 w-4" />
          </IconButton>
          <IconButton
            type="button"
            size="sm"
            title="Next page"
            aria-label="Next page"
            disabled={numPages === 0 || pageNumber >= numPages || loading}
            onClick={() => scrollToPage(pageNumber + 1)}
          >
            <ChevronDown className="h-4 w-4" />
          </IconButton>
          <span className="ml-2 min-w-[4.5rem] rounded-md border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-2 py-1 text-center text-xs text-[var(--lm-foreground-strong)] dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-200">
            {numPages > 0 ? `${pageNumber} / ${numPages}` : "—"}
          </span>
        </ActionButtonGroup>

        <ActionButtonGroup>
          <IconButton
            type="button"
            size="sm"
            title="Zoom out"
            aria-label="Zoom out"
            disabled={zoomIndex <= 0 || loading}
            onClick={() => setZoomIndex((index) => Math.max(index - 1, 0))}
          >
            <ZoomOut className="h-4 w-4" />
          </IconButton>
          <span className="min-w-[3.25rem] rounded-md border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-2 py-1 text-center text-xs text-[var(--lm-foreground-strong)] dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-200">
            {Math.round(zoom * 100)}%
          </span>
          <IconButton
            type="button"
            size="sm"
            title="Zoom in"
            aria-label="Zoom in"
            disabled={zoomIndex >= ZOOM_STEPS.length - 1 || loading}
            onClick={() =>
              setZoomIndex((index) => Math.min(index + 1, ZOOM_STEPS.length - 1))
            }
          >
            <ZoomIn className="h-4 w-4" />
          </IconButton>
        </ActionButtonGroup>

        <ActionButtonGroup>
          <IconButton
            type="button"
            size="sm"
            title="Rotate"
            aria-label="Rotate"
            disabled={loading || !!iframeUrl}
            onClick={() => setRotation((value) => (value + 90) % 360)}
          >
            <RotateCw className="h-4 w-4" />
          </IconButton>
          <IconButton
            type="button"
            size="sm"
            title="Download"
            aria-label="Download"
            disabled={!file && !iframeUrl}
            onClick={() => (file ? handleDownload() : void handleOpenExternal())}
          >
            <Download className="h-4 w-4" />
          </IconButton>
          <IconButton
            type="button"
            size="sm"
            title="Open in new tab"
            aria-label="Open in new tab"
            onClick={() => void handleOpenExternal()}
          >
            <ExternalLink className="h-4 w-4" />
          </IconButton>
        </ActionButtonGroup>
      </div>

      <div
        ref={canvasRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-[#1e293b]/40"
      >
        {loading ? (
          <div className="flex min-h-[240px] items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
          </div>
        ) : iframeUrl ? (
          <iframe
            title={displayTitle}
            src={iframeUrl}
            className="h-full min-h-[480px] w-full border-0 bg-[#1e293b]/40"
          />
        ) : error || !file ? (
          <div className="flex min-h-[240px] flex-col items-center justify-center gap-4 px-4 py-16 text-center">
            <p className="text-sm text-red-300">{error ?? "Failed to load PDF"}</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleOpenExternal()}
            >
              <ExternalLink className="h-4 w-4" />
              Open in new tab
            </Button>
          </div>
        ) : (
          <div
            ref={pagesColumnRef}
            className="flex flex-col items-center gap-4 px-4 py-6"
          >
            <Document
              file={file}
              onLoadSuccess={({ numPages: loadedPages }) => {
                setNumPages(loadedPages);
                const startPage = Math.min(
                  Math.max(targetInitialPageRef.current || initialPage, 1),
                  loadedPages,
                );
                targetInitialPageRef.current = startPage;
                setPageNumber(startPage);
                pendingScrollPage.current = startPage;
                initialScrollDoneRef.current = false;
                highlightScrolledRef.current = false;
              }}
              onLoadError={(loadError) => {
                setError(loadError.message || "Failed to render PDF");
              }}
              loading={
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
                </div>
              }
            >
              {pageNumbers.map((page) => (
                <div
                  key={page}
                  ref={(element) => {
                    pageRefs.current[page - 1] = element;
                  }}
                  data-page={page}
                  className="flex w-full justify-center"
                >
                  <Page
                    pageNumber={page}
                    renderTextLayer
                    renderAnnotationLayer
                    rotate={rotation}
                    className="rounded-sm shadow-[0_4px_24px_rgba(0,0,0,0.35)]"
                    width={pageWidth}
                    onRenderSuccess={() => {
                      const pending = pendingScrollPage.current;
                      if (pending != null && page <= pending) {
                        scrollToPageWhenReady(pending);
                      }
                    }}
                    onRenderTextLayerSuccess={() => {
                      if (page === (targetInitialPageRef.current || initialPage)) {
                        applyExcerptHighlight();
                      }
                    }}
                  />
                </div>
              ))}
            </Document>
          </div>
        )}
      </div>
    </div>
  );
}
